/**
 * "Read this mail with AI" — `mail.extract_enquiry_items` is an ASYNC job, not a synchronous
 * call. See `mailExtractionState.ts`'s header for the full story: the method returns `{job_id}`
 * immediately and delivers its result later, over Socket.io, as `extract.done`. This file used
 * to treat the response as `{items: [...]}`, found no `items` on the real `{job_id}` reply, and
 * reported "Found 0 line(s)" for every mail — reading absence-of-data-yet as proof-of-zero.
 *
 * ---- What runs where, and why ----
 *
 * The LLM call itself is server-side, necessarily — the provider token can never be in browser
 * code, and `CLAUDE.md` assigns "AI extraction + confidence" to the backend (Q8/P-12). This
 * module submits the job (just a communication id — the real backend reads its own mail/File
 * storage) and waits for the answer; it does no reading of its own.
 *
 * ---- Two wire shapes, one server call ----
 *
 * The mock (`mock-server/bosun/handlers/mail.ts`) answers `extract_enquiry_items` SYNCHRONOUSLY
 * — `{enquiry_type, customer_hint, items, notes}` directly, no job, no socket — because the mock
 * has no queue and no socket server (`useRealtime.ts`'s header). The live backend answers
 * `{job_id}` and needs the wait. `runExtraction` below branches on which shape actually came
 * back rather than assuming one, so local dev against the mock keeps working exactly as before
 * while the live path is now correct.
 *
 * ---- The local fallback (restored) ----
 *
 * The previous version of this file said the fallback below "cannot rescue anything" because
 * `quoflow.api.extract_enquiry_from_mail` was believed to be a stale reference to a method the
 * backend never deployed. That was wrong — it is **our own mock endpoint**
 * (`mock-server/frappe/extract.ts`), not a live-server method, and it still works. The real bug
 * (`docs/bosun-frappe-asks.md` `B1`) is that `mail.extract_enquiry_items`'s job dies in 38ms on a
 * stale module path on THEIR side — nothing here can fix that, but it also means the fallback was
 * never actually useless; it was withdrawn on a mistaken premise.
 *
 * The PRIMARY path is unchanged and still runs FIRST, every time — submit, and for the real
 * backend's async shape, wait out the full timeout for `extract.done`. Only once that has
 * genuinely failed (`submit-error`, `socket-unavailable`, or `timeout`) does `runExtraction` fall
 * back: read the mail's attachments client-side (the mock has no access to the live site's
 * `/private/files`; the browser already does — see `prepareAttachment` below) and call
 * `quoflow.api.extract_enquiry_from_mail`, an LLM call that runs server-side in the mock so the
 * provider token never reaches the browser. `result.source` records which path actually
 * answered — `'server'` or `'local'` — so a caller CAN badge a local result as the stopgap it is
 * rather than presenting it identically to a genuine server read. `CreateEnquiryBar.tsx` — the
 * one-line confirmation bar that replaced the old triage form — does not currently render this
 * distinction; the field is still on `ExtractionResult` for whichever caller needs it next.
 *
 * When the backend fixes `B1`, the primary path starts succeeding again and this fallback simply
 * stops being reached — no code change needed on this side.
 *
 * ---- Extraction is a suggestion, never a commitment ----
 *
 * Nothing here writes. The result pre-fills a form the purchaser then reviews, and `confidence`
 * is the model's own claim rather than a calibrated probability — the UI must say so.
 *
 * Every code the model returns is checked against the real `Item` master before it reaches the
 * form — see `verifyItemCodes`, which exists because a faithfully-copied code can still be one
 * this ERP does not have, and an unknown Link value fails the whole enquiry create.
 *
 * Every UOM the model returns gets the SAME treatment — see `verifyUoms` below. `uom` is a Link
 * to the `UOM` master too, and `"NO"`/`"Pcs"`/whatever the customer typed can just as easily be
 * one this ERP does not have (`Could not find Row #1: UOM: NO`, the crash this fix closes).
 */

import { useCallback, useRef, useState, useSyncExternalStore } from 'react'
import { getResource } from './frappe'
import type { SheetRow } from '../sheetExtract'
import { describeRead, isProbablyDecoration, readMailItems, type ReadableAttachment, type ReadItemsResult } from '../readMailItems'
import { analyzeLayout, diSheets, diText, visionAvailable, type DiAnalyzeResult } from '../documentIntelligence'
import { extractLinesFromBody, mailReaderAvailable } from '../mailBodyExtract'
import { rescueText } from '../mailRescue'
import type { SheetItem } from '../sheetExtract'
import { isLegacyXls } from '../legacyXls'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { downloadAttachmentBlob, useCachedAttachmentLoader, type BlobLoader } from './attachmentBlob'
import { fetchUomMaster, uomMasterQuery } from './useUoms'
import { resolveUom } from '../uom'
import { getPhase, resetRun, startRun, subscribe } from './extractionStore'
import {
  IDLE_PHASE,
  type ExtractedLine,
  type ExtractionAction,
  type ExtractionFailureReason,
  type ExtractionPhase,
  type ExtractionResult,
} from './mailExtractionState'
import type { MailMessage } from '../types'
import { pdfTextReadLooksPoor } from '../pdfReadQuality'

export type { ExtractedLine, ExtractionResult, ExtractionPhase, ExtractionFailureReason } from './mailExtractionState'

/** The PRIMARY path: `mail.extract_enquiry_items`. Tried first, always — see the module header. */
export const EXTRACT_ENQUIRY_ITEMS_MODULE = 'mail'
export const EXTRACT_ENQUIRY_ITEMS_METHOD = 'extract_enquiry_items'

/** `<module>.<method>` — used only in diagnostic logs below, never for the actual request. */

/** The FALLBACK path — PROPOSED, implemented only in the mock (`mock-server/frappe/extract.ts`).
 * Never proxied live by default; see `vite.config.ts`'s one narrow exception for this exact path,
 * which routes it to the mock even in all-live dev, commented as the deliberate stopgap it is. */
export const EXTRACT_METHOD = 'quoflow.api.extract_enquiry_from_mail'

/** How long to wait for `extract.done` before giving up on the primary path and trying the local
 * fallback. An AI extraction job, not a request/response call — long enough to cover a slow
 * model, short enough that "Reading…" does not sit forever on an indefinite spinner. */
export const EXTRACTION_TIMEOUT_MS = 90_000

/** One line as the SYNCHRONOUS (mock-only) reply shapes it — see the module header. */
/** The async, real-backend reply: nothing but a job id. */


/**
 * Check every code the model returned against the real `Item` master, and blank the ones that do
 * not exist.
 *
 * **This is not belt-and-braces, it is required.** The model is instructed never to invent a code,
 * and it obeys — but a code copied faithfully out of a customer's spreadsheet can still be one
 * this ERP has never heard of. The real RFQ workbook is exactly that case: of its three IMPA
 * codes, `0100000008` and `2302010001TEST` exist and `3901010001TEST` does not.
 *
 * `Opportunity Item.item_code` is a Link, so an unknown code fails validation and takes the WHOLE
 * enquiry down with it — not just that row. Blanking it here turns a create that would have been
 * rejected into a row the purchaser can resolve with the inline search box, keeping the
 * description the model read so they know what they are matching.
 */
/** Cached per exact code set — re-extracting the same mail (the common case while `B1` forces
 * the fallback) then costs nothing. Sorted so key order can't split the entry. */
const itemCodeCheckQuery = (codes: string[]) => ({
  queryKey: ['item-code-check', [...codes].sort().join(',')] as const,
  queryFn: async () => {
    const page = await getResource<Record<string, unknown>>('Item', {
      fields: ['name'],
      filters: [['name', 'in', codes] as [string, string, unknown]],
      limit: codes.length,
    })
    return page.data.map((row) => String(row.name ?? ''))
  },
  staleTime: 5 * 60_000,
})

/**
 * Which of these codes the Item master actually has — for any caller about to write
 * `item_code` (the upload button, 2026-09-17). Throws when the lookup itself fails, so the
 * caller decides between "cannot check" and "checked and unknown".
 */
export async function knownItemCodes(codes: readonly string[], queryClient?: QueryClient): Promise<Set<string>> {
  const unique = [...new Set(codes)]
  if (unique.length === 0) return new Set()
  const query = itemCodeCheckQuery(unique)
  return new Set(queryClient ? await queryClient.fetchQuery(query) : await query.queryFn())
}

async function verifyItemCodes(
  items: ExtractedLine[],
  queryClient?: QueryClient,
): Promise<{ items: ExtractedLine[]; rejected: string[] }> {
  const codes = [...new Set(items.map((i) => i.item_code).filter((c): c is string => !!c))]
  if (codes.length === 0) return { items, rejected: [] }

  let known: Set<string>
  try {
    const query = itemCodeCheckQuery(codes)
    known = new Set(queryClient ? await queryClient.fetchQuery(query) : await query.queryFn())
  } catch {
    // If the lookup itself fails we cannot prove a code is bad, and silently blanking every line
    // would be worse than letting the server have the final say. Leave them as-is.
    return { items, rejected: [] }
  }

  const rejected: string[] = []
  const checked = items.map((item) => {
    if (!item.item_code || known.has(item.item_code)) return item
    rejected.push(item.item_code)
    // Keep the code in the description so the purchaser can see what the mail actually said.
    return { ...item, item_code: null, description: `${item.description} (code ${item.item_code} not in catalogue)` }
  })
  return { items: checked, rejected }
}

/**
 * Check every UOM the model returned against the real `UOM` master, and blank the ones it does
 * not recognise — the exact same treatment `verifyItemCodes` gives `item_code`, for the exact
 * same reason: `Enquiry Item.uom` is a Frappe Link, so an unverified value fails the WHOLE
 * enquiry create, not just its row (`Could not find Row #1: UOM: NO` — the bug this closes).
 *
 * Resolution itself (`resolveUom`, `../uom`) is exact-then-case-insensitive ONLY — no alias
 * table. `"NO"` does not become `"Nos"` here just because that looks like an obvious human
 * correction; see that function's own header for why guessing a unit is worse than asking.
 *
 * A resolved value is normalised to the MASTER's own spelling (never the raw text) so what a
 * caller eventually sends is exactly the Link value the server will accept. An unresolved one is
 * blanked to `null` with the original text preserved on `raw_uom`, so a caller can show the
 * purchaser what they're translating. `CreateEnquiryBar.tsx` does not show this today — an
 * unresolved line travels through `create_enquiry` with `uom: null` and is left for the RFQ
 * builder's own triage, not resolved inline here.
 */
/* NOTE 2026-08-06: a blanked `uom` does NOT reach the server as `null` — that field is mandatory
   on the child row (measured). `features/mail/createEnquiryLines.ts` substitutes a placeholder and
   unmatches the line so the substitution cannot hide inside a finished-looking row. */
async function verifyUoms(
  items: ExtractedLine[],
  queryClient?: QueryClient,
): Promise<{ items: ExtractedLine[]; unresolved: string[] }> {
  const hasAnyUom = items.some((item) => item.uom && item.uom.trim() !== '')
  if (!hasAnyUom) return { items, unresolved: [] }

  let master: string[]
  try {
    // Shared with `useUomMaster` — the same ~39 rows this used to re-fetch on every extraction.
    master = queryClient ? await fetchUomMaster(queryClient) : await uomMasterQuery.queryFn()
  } catch {
    // Same caution `verifyItemCodes` uses: a failed lookup cannot prove any of these are bad, so
    // leave them exactly as extracted rather than blanking on no evidence.
    return { items, unresolved: [] }
  }

  const unresolved: string[] = []
  const checked = items.map((item) => {
    if (!item.uom || item.uom.trim() === '') return item
    const resolved = resolveUom(item.uom, master)
    if (resolved !== null) return resolved === item.uom ? item : { ...item, uom: resolved }
    unresolved.push(item.uom)
    return { ...item, uom: null, raw_uom: item.uom }
  })
  return { items: checked, unresolved }
}

/**
 * Turn the model's `customer_hint` into REAL `Customer` docnames.
 *
 * The hint is prose off a letterhead — `"AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)"` — and never
 * matches a docname character for character, so on its own it is unusable: the field stayed empty
 * while the panel said "Customer looks like …", which is the worst of both.
 *
 * Resolution is exact-first, then progressively wider: punctuation is dropped and the leading
 * words are used as a `like` pattern, starting with the MOST words (narrowest) and shortening
 * until something matches. On the real mail, four words (`AQUA MARIS SHIPS SUPPLY`) return exactly
 * one customer, where two words return two — which is precisely why it widens rather than
 * narrows.
 *
 * Returns every match, and the caller decides: one match is filled in, several are offered as
 * one-click choices. It never picks between candidates itself — on this very mail the hint is the
 * letterhead of the CHANDLER, not the buyer, so a confident auto-pick would put the wrong company
 * on a real enquiry.
 */
async function resolveCustomerHint(hint: string | null): Promise<string[]> {
  if (!hint || hint.trim() === '') return []
  try {
    const exact = await getResource<Record<string, unknown>>('Customer', {
      fields: ['name'],
      filters: [['name', '=', hint.trim()]],
      limit: 1,
    })
    if (exact.data.length === 1) return [String(exact.data[0].name)]

    const words = hint
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 1)
    for (let count = Math.min(words.length, 4); count >= 2; count--) {
      const term = words.slice(0, count).join(' ')
      const page = await getResource<Record<string, unknown>>('Customer', {
        fields: ['name'],
        filters: [['name', 'like', `%${term}%`]],
        limit: 6,
      })
      if (page.data.length > 0) return page.data.map((row) => String(row.name))
    }
  } catch {
    // A failed lookup is not evidence of anything — leave the field to the purchaser.
    return []
  }
  return []
}

/** The plain shape either path reduces to before item-code verification / customer-hint
 * resolution / stamping `source`. */
interface ExtractionBase {
  enquiry_type: 'Provision' | 'Technical' | null
  customer_hint: string | null
  items: ExtractedLine[]
  notes: string
}

/** Item-code verification + customer-hint resolution, shared by both wire shapes once each has
 * been reduced to a plain `enquiry_type`/`customer_hint`/`items`/`notes` base. `source` is stamped
 * on here rather than threaded through every caller — see `ExtractionResult.source`'s doc comment
 * for why the UI must not treat the two the same. */
/**
 * A list of unmatched values, said once each with how many lines use it.
 *
 * These notes are per-LINE, so a 228-row spreadsheet reported all 186 of its unmatched units
 * individually — "PACK, PACK, PACK, … KILOGRAM, KILOGRAM …" filling eighteen lines of the screen
 * for what is really seven distinct units (reported 2026-09-05). The repetition buried the count
 * that matters and pushed the note about unmatched item CODES off the top of the box.
 *
 * Commonest first, because that is the one worth adding to the master. Capped at ten with the
 * remainder counted — a note is a prompt to act, not an export.
 */
const SUMMARY_LIMIT = 10

export function summariseUnmatched(values: readonly string[]): string {
  const counts = new Map<string, number>()
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1)

  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  // A count on a value that appears once is noise: "CARTON (1)" says nothing "CARTON" does not.
  const named = ranked.slice(0, SUMMARY_LIMIT).map(([value, n]) => (n > 1 ? `${value} (${n})` : value))
  const rest = ranked.length - named.length

  return rest > 0 ? `${named.join(', ')} and ${rest} more` : named.join(', ')
}


async function buildExtractionResult(
  base: ExtractionBase,
  source: 'server' | 'local',
  queryClient?: QueryClient,
): Promise<ExtractionResult> {
  const [{ items: codeChecked, rejected }, customerMatches] = await Promise.all([
    verifyItemCodes(base.items, queryClient),
    resolveCustomerHint(base.customer_hint),
  ])
  // Sequenced after item-code verification (not run in the same `Promise.all`) only because it
  // operates on `codeChecked` — the two checks touch disjoint fields (`item_code` vs `uom`) and
  // neither depends on the other's OUTCOME, but composing two independent array transforms via a
  // per-index merge is more error-prone than one extra `await`.
  const { items, unresolved } = await verifyUoms(codeChecked, queryClient)

  const notes = [
    base.notes,
    rejected.length
      ? `${rejected.length} code(s) from the mail are not in the catalogue and need matching: ${summariseUnmatched(rejected)}.`
      : '',
    unresolved.length
      ? `${unresolved.length} unit(s) from the mail are not in the UOM master and need matching: ${summariseUnmatched(unresolved)}.`
      : '',
  ]
    .filter((part) => part.trim() !== '')
    .join(' ')
    .trim()

  return {
    enquiry_type: base.enquiry_type,
    customer_hint: base.customer_hint,
    items,
    notes,
    customer_matches: customerMatches,
    source,
  }
}

/* ============================================================================================
 * The local fallback — client-side attachment reading + `quoflow.api.extract_enquiry_from_mail`.
 * See the module header, "The local fallback". Only reached after the primary path has failed.
 * ==========================================================================================*/

/** One attachment prepared for the fallback call. */

/** A `waitForExtractDone` in progress: the promise it settles, plus a way to tear it down without
 * waiting for either the event or the timeout. See the function's own header for why this exists. */

/**
 * Wait for the next `extract.done` payload that parses as the documented shape, or give up after
 * `timeoutMs`. A payload that does not parse (some other/future event on the same bus, a
 * malformed message) is skipped, not treated as an answer — the wait continues.
 *
 * This is the "scope the wait to the extraction just started" mitigation `mailExtractionState.ts`
 * describes: the subscription exists only for the duration of one attempt — either it resolves
 * itself (the event arrives, or `timeoutMs` elapses) or the caller calls `cancel()`, which
 * `runExtraction`'s dead-job branch does the moment `pollJob` wins the race instead. Without that
 * call the subscription and the 90s timer would both outlive the attempt that started them — see
 * `useExtractFromMail`'s effect-free wiring below, which never subscribes outside that window. It
 * is still not true correlation (no field ties an `extract.done` back to a `job_id` — see the
 * module header), only a best-effort narrowing of which event gets accepted.
 */

export interface RunExtractionOptions {
  dispatch: (action: ExtractionAction) => void
  timeoutMs?: number
  now?: () => number
  /**
   * Shared caches. Both are optional and everything still works without them — they only remove
   * duplicate network work:
   *
   * - `queryClient` lets the `UOM` and `Item` master checks read the same cache entries the rest
   *   of the app already fills, instead of re-fetching ~39 UOM rows on every single extraction.
   * - `loadBlob` shares the attachment download with `AttachmentPreview.tsx`, so previewing a
   *   spreadsheet and THEN reading the mail with AI costs one download rather than two.
   */
  queryClient?: QueryClient
  loadBlob?: BlobLoader
  /**
   * Azure Document Intelligence for a photographed requisition or a scanned PDF (2026-09-17) —
   * the same layer the upload button has (`readDocument.ts`). Injectable so the reader is
   * testable without the network; the default is the live proxy.
   */
  vision?: VisionReader
  /**
   * The LLM reader, reached when the column matchers find nothing (2026-09-17) — see
   * `mailRescue.ts`. Injectable for the tests; the default is the `/llm` proxy, and `[]` on a
   * deployment without it, which leaves the old "could not read" report in place.
   */
  readProse?: (text: string) => Promise<SheetItem[]>
}

export interface VisionReader {
  available: () => Promise<boolean>
  analyze: (bytes: Uint8Array) => Promise<DiAnalyzeResult>
}

const LIVE_VISION: VisionReader = { available: visionAvailable, analyze: (bytes) => analyzeLayout(bytes) }
const liveReadProse = async (text: string): Promise<SheetItem[]> => ((await mailReaderAvailable()) ? extractLinesFromBody(text) : [])

/**
 * Drives one extraction attempt end to end, reporting every step through `dispatch` rather than
 * returning a value — the caller (the hook) needs to know about the `pending`/`falling-back`
 * phases WHILE this is still running, not just its eventual outcome. Exported ambient of React so
 * it can be unit tested directly (this repo's vitest config runs in a `node` environment with no
 * DOM — see `mailExtractionState.test.ts` and `useMailExtraction.test.ts`).
 */
export async function runExtraction(mail: MailMessage, options: RunExtractionOptions): Promise<void> {
  const { dispatch } = options

  dispatch({ type: 'submit' })

  /*
    Read HERE, from the mail already on screen. There is no server call, no queued job and no
    socket to wait on — see this file's header.

    What used to happen: POST `mail.extract_enquiry_items`, receive a `{job_id}`, then wait up to
    90 seconds for an `extract.done` event that never came, because the job died on a stale module
    path (`B1`). The whole apparatus existed to survive a backend that was never fixed and is not
    going to be.
  */
  const { result: firstRead, readable: attachments } = await readAttachmentsAndBody(mail, options.loadBlob, options.vision ?? LIVE_VISION)
  let read = firstRead
  let rescued = false

  /*
    THE AI RESCUE (Akhil, 2026-09-17). The column matchers read nothing — a sheet whose headings
    they cannot place, a list typed as prose, a photo the OCR turned into text — and this used to
    stop here at "could not read this mail". Now everything the mail carries goes to the LLM
    reader as one document (`rescueText`), and its lines come back for review like any AI read.
    Nothing is invented: a model that finds nothing leaves the original report in place.
  */
  if (!read.readable || read.items.length === 0) {
    const text = rescueText({ subject: mail.subject, body_html: mail.body_html ?? '' }, attachments)
    if (text !== '') {
      let items: SheetItem[] = []
      try {
        items = await (options.readProse ?? liveReadProse)(text)
      } catch {
        items = []
      }
      if (items.length > 0) {
        read = { ...read, items, readable: true, source: read.source ?? 'attachments' }
        rescued = true
      }
    }
  }

  if (!read.readable) {
    dispatch({ type: 'failed', reason: 'unreadable', message: describeRead(read) })
    return
  }

  const result = await buildExtractionResult(
    {
      enquiry_type: null,
      customer_hint: null,
      items: read.items.map((item) => ({
        item_code: item.item_code,
        description: item.description,
        qty: item.qty,
        uom: item.uom,
        /*
          PROVENANCE, carried through. `groupLinesBySheet` splits on exactly these two fields, so
          dropping them here quietly disabled the whole "one enquiry per sheet" choice: every line
          landed in one anonymous group, `canSplit` saw a single draft, and the radio never
          appeared. A workbook with a deck sheet and an engine sheet offered one merged enquiry and
          no way to say otherwise — the feature was built, and the data never reached it.
        */
        sheet: item.sheet,
        file: item.file,
        // Read from a labelled column or an unambiguous line, not inferred — but still shown for
        // review, because a mis-read column is exactly what this cannot detect in itself. An AI
        // rescue is a model's reading: below 1, so every row carries its bar.
        confidence: rescued ? 0.8 : 1,
      })),
      notes: rescued
        ? `Read ${read.items.length} line${read.items.length === 1 ? '' : 's'} with the AI reader — the mail's tables and text could not be read by matching columns, so check each line against the mail.${
            read.skipped.length ? ` Not read: ${read.skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}.` : ''
          }`
        : describeRead(read),
    },
    'local',
    options.queryClient,
  )

  dispatch({ type: 'succeeded', result })
}

/**
 * A workbook reader's answer, shaped into sheets — or nothing, if it is not that shape.
 *
 * Deliberately strict. The bug this replaces mapped a list of ROWS into a list of sheets with no
 * data, and every one of them read as empty: the failure looked exactly like a file with no items
 * in it, which is the one thing it must never be confused with. An entry without a `data` array is
 * not a sheet, and saying so is better than reading nothing and blaming the customer's file.
 */
function toSheets(raw: unknown): { name: string; rows: SheetRow[] }[] {
  if (!Array.isArray(raw)) return []
  const sheets = raw.filter(
    (entry): entry is { sheet?: string; data: SheetRow[] } =>
      typeof entry === 'object' && entry !== null && Array.isArray((entry as { data?: unknown }).data),
  )
  return sheets.map((entry, i) => ({ name: entry.sheet ?? `Sheet${i + 1}`, rows: entry.data }))
}

/**
 * Open every attachment we can read and hand the parsed rows/text to `readMailItems`.
 *
 * The DOWNLOADING and DECODING live here because they need the browser's session and its lazy
 * imports; the deciding lives in `lib/readMailItems.ts`, which is pure and tested against every
 * real attachment on the bench.
 */
async function readAttachmentsAndBody(
  mail: MailMessage,
  loadBlob?: BlobLoader,
  vision: VisionReader = LIVE_VISION,
): Promise<{ result: ReadItemsResult; readable: ReadableAttachment[] }> {
  const readable: ReadableAttachment[] = []
  // Asked once per mail, and only when an image or a scan actually needs it.
  let visionOn: boolean | null = null
  const canSee = async () => (visionOn ??= await vision.available())
  /**
   * OCR one attachment — its tables as sheets, else its text, else nothing (`null`). The caller
   * files the outcome; this never throws past a read that failed.
   */
  const see = async (blob: Blob): Promise<ReadableAttachment['sheets'] | { text: string } | null> => {
    try {
      const result = await vision.analyze(new Uint8Array(await blob.arrayBuffer()))
      const sheets = diSheets(result)
      if (sheets.length > 0) return sheets
      const text = diText(result).trim()
      return text === '' ? null : { text }
    } catch {
      return null
    }
  }

  for (const attachment of mail.attachments) {
    /*
      A PHOTOGRAPHED requisition (2026-09-17) — the same Document Intelligence read the upload
      button has. A signature-sized image is left as decoration (`isProbablyDecoration`), and a
      deployment without the OCR reports the photo by name as before, never a silent skip.
    */
    if (attachment.kind === 'img') {
      const asIs: ReadableAttachment = { name: attachment.name, kind: 'img', size: attachment.size }
      if (isProbablyDecoration(asIs) || !attachment.preview_url || !(await canSee())) {
        readable.push(asIs)
        continue
      }
      let blob: Blob
      try {
        blob = loadBlob ? await loadBlob(attachment.preview_url) : await downloadAttachmentBlob(attachment.preview_url)
      } catch {
        readable.push(asIs)
        continue
      }
      const seen = await see(blob)
      if (Array.isArray(seen)) readable.push({ name: attachment.name, kind: 'xlsx', sheets: seen })
      else if (seen) readable.push({ name: attachment.name, kind: 'pdf', text: seen.text })
      else readable.push(asIs)
      continue
    }
    // `doc` joins the readable kinds 2026-09-09 — see the Word branch below.
    if (attachment.kind !== 'xlsx' && attachment.kind !== 'pdf' && attachment.kind !== 'doc') {
      readable.push({ name: attachment.name, kind: attachment.kind, size: attachment.size })
      continue
    }
    if (!attachment.preview_url) {
      readable.push({ name: attachment.name, kind: 'other' })
      continue
    }

    let blob: Blob
    try {
      blob = loadBlob ? await loadBlob(attachment.preview_url) : await downloadAttachmentBlob(attachment.preview_url)
    } catch {
      readable.push({ name: attachment.name, kind: 'other' })
      continue
    }

    if (attachment.kind === 'pdf') {
      let text = ''
      try {
        const { extractText, getDocumentProxy } = await import('unpdf')
        const pdf = await getDocumentProxy(new Uint8Array(await blob.arrayBuffer()))
        const extracted = await extractText(pdf, { mergePages: true })
        text = Array.isArray(extracted.text) ? extracted.text.join('\n') : String(extracted.text ?? '')
      } catch {
        text = ''
      }
      /*
        No text layer (a scan) — OR a text layer the reader can make nothing of (2026-09-18:
        "426 09 26 225E Exhaust Gas Pyrometers.pdf", a form whose columns come out scrambled).
        Either way the OCR reads the PAGE, table structure included, the way it reads a photo.
      */
      const textRead = text.trim() === '' ? 0 : readMailItems('', [{ name: attachment.name, kind: 'pdf', text }]).items.length
      /*
        … or a text layer the reader did a POOR job of (2026-09-21, PO_POSXTV-000126: nine
        numbered items over five lines each, the prose reader assembled five wrong ones and that
        counted as "found something"). `pdfTextReadLooksPoor` compares the read with the
        customer's own numbering. DI's TABLE read wins only when it has at least as many lines;
        a page it makes less of stays with the text read.
      */
      /*
        Akhil, 2026-09-22: "when there is doubt … better to use DI — accuracy matters". So not
        only the failed or poor read: EVERY PDF goes to DI when it is configured, and the table
        wins on the same rule. (`pdfTextReadLooksPoor` still decides whether a poor text read
        may stand in when DI is absent — see below.)
      */
      const readsAsNothing = textRead === 0
      if (await canSee()) {
        const seen = await see(blob)
        if (Array.isArray(seen)) {
          const tableRead = readMailItems('', [{ name: attachment.name, kind: 'xlsx', sheets: seen }]).items.length
          if (tableRead >= textRead) {
            readable.push({ name: attachment.name, kind: 'xlsx', sheets: seen })
            continue
          }
        } else if (seen && readsAsNothing) {
          text = seen.text
        }
      }
      // No DI (or DI read less): a poor column read is withheld so the AI rescue gets the text.
      readable.push({ name: attachment.name, kind: 'pdf', text, unreliable: pdfTextReadLooksPoor(text, textRead) })
      continue
    }

    /*
      WORD `.doc` — the other OLE2 format, and the one that used to be reported as unreadable.

      Six real requisitions on this bench are `.doc`, and the server-side extractor that would
      otherwise handle them is dead (`B1`). `legacyDoc.ts` reads the piece table and reconstructs
      the table rows, producing the same `LegacySheet[]` a spreadsheet does — so everything
      downstream works unchanged. Dynamically imported, sharing SheetJS's container reader with
      the `.xls` path and adding no dependency.

      It REFUSES a document with no table shape (a letter, a form of merged cells) rather than
      cutting the text into rows at arbitrary places; that lands here as `kind: 'other'`, which
      reports the file as not read instead of inventing lines from it.
    */
    if (attachment.kind === 'doc') {
      try {
        const { parseLegacyDoc } = await import('../legacyDoc')
        const sheets = await parseLegacyDoc(new Uint8Array(await blob.arrayBuffer()), attachment.name)
        if (sheets.length > 0) {
          readable.push({ name: attachment.name, kind: 'xlsx', sheets: sheets.map((s) => ({ name: s.name, rows: s.rows })) })
          continue
        }
      } catch {
        // Falls through to `other` — a Word file we could not read is not an error to raise at
        // the purchaser, it is one line in the "not read" note.
      }
      readable.push({ name: attachment.name, kind: 'other' })
      continue
    }

    try {
      /*
        LEGACY `.xls` FIRST — decided on the bytes, not the filename.

        These used to fall through to the `catch` below and be filed as `kind: 'other'`, i.e. read
        as nothing: one file in fifteen on this bench, and "TT-04-DCK Request List - DECK.xls" is a
        real requisition. `read-excel-file` refuses OLE2 by design, so it gets its own parser —
        dynamically imported, so the 7.5MB only loads when a legacy file actually turns up.
      */
      const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer())
      if (isLegacyXls(head)) {
        const { parseLegacyXls } = await import('../legacyXls')
        const legacy = await parseLegacyXls(new Uint8Array(await blob.arrayBuffer()))
        const legacySheets = legacy.map((sheet) => ({ name: sheet.name, rows: sheet.rows }))
        if (legacySheets.length === 0) {
          readable.push({ name: attachment.name, kind: 'other' })
          continue
        }
        readable.push({ name: attachment.name, kind: 'xlsx', sheets: legacySheets })
        continue
      }

      /*
        EVERY sheet, not just the first — and via the DEFAULT export, not `readSheet`.

        `readSheet(blob, { getSheets: true })` silently ignores the option and returns ROWS. That
        shipped: `FORM 19-071 BATTERY.xlsx` reported "no item table on any of its 47 sheets",
        47 being its row count, because each row was mapped to a sheet with no data in it. Reading
        stopped working altogether for spreadsheets and the message blamed the file.
      */
      const { default: readWorkbook } = await import('read-excel-file/universal')
      const raw = (await readWorkbook(blob, { getSheets: true } as never)) as unknown

      const sheets = toSheets(raw)
      if (sheets.length === 0) {
        readable.push({ name: attachment.name, kind: 'other' })
        continue
      }
      readable.push({ name: attachment.name, kind: 'xlsx', sheets })
    } catch {
      // A spreadsheet neither parser could open. Filed as `other` so `readMailItems` reports it as
      // skipped BY NAME rather than the mail appearing to have no attachment worth reading.
      readable.push({ name: attachment.name, kind: 'other' })
    }
  }

  // The opened attachments travel with the result: the AI rescue reads them whole when the
  // column matchers could not.
  return { result: readMailItems(mail.body_html, readable), readable }
}

export interface UseExtractFromMailOptions {
  onSuccess?: (result: ExtractionResult) => void
  onError?: (error: { reason: ExtractionFailureReason; message: string }) => void
}

export interface UseExtractFromMailResult {
  /** The full state — read `phase.status` to distinguish `pending`/`failed` from a genuine
   * zero-item `succeeded`. Never collapse this to a boolean. */
  phase: ExtractionPhase
  /** `true` for `submitting` (still talking to the primary), `pending` (job queued, waiting on
   * the socket), and `falling-back` (primary failed, local fallback now running) — the button
   * should stay disabled and show a busy state for all three. */
  isPending: boolean
  mutate: (mail: MailMessage) => void
  reset: () => void
}

/**
 * Start an extraction in the background, silently. Fire-and-forget: no spinner, no toast, no
 * error surfaced. If it fails the click path runs normally and reports its own failure.
 *
 * Called when the attachment preview opens for a mail the classifier is confident about — see
 * `src/lib/mailPrewarm.ts` for that decision, which is deliberately not made here.
 */
export function prewarmExtraction(
  mail: MailMessage,
  deps: { queryClient?: QueryClient; loadBlob?: BlobLoader },
): void {
  void startRun(mail.id, (dispatch) =>
    runExtraction(mail, {
      dispatch,
      queryClient: deps.queryClient,
      loadBlob: deps.loadBlob,
    }),
  )
}

/**
 * `mutate`'s actual engine: start (or join, or short-circuit — see below) a run for `mail`, then
 * read whatever the store ends up holding and hand it to `callbacks`, each firing at most once.
 * Exported and React-free so it is unit-testable without mounting the hook (no component-test
 * infra in this repo — see the module header).
 *
 * ---- The bug this fixes (fix round 1, `task-4-report.md`) ----
 *
 * The previous version drove `onSuccess`/`onError` off `startRun`'s `hooks.onAction` — but that
 * hook only fires for the run a `startRun` call actually CREATES (`extractionStore.ts`:
 * `if (entry.promise) return entry.promise` returns before `hooks` is ever looked at). A `mutate`
 * that JOINS a run already in flight — the exact case `prewarmExtraction` above exists to set up —
 * got no callback at all: the run finished, the store flipped to `succeeded`, the button
 * re-enabled, and `onSuccess` never fired. Silent, and easy to misread as flakiness, because a
 * second click "worked" (by then the store WAS `succeeded`, no run to join).
 *
 * The fix is to stop reading side effects off the run's own internal dispatch stream and instead
 * await the promise `startRun` returns and look at the RESULT. That promise resolves the same way
 * regardless of whether this call created the run, joined one already going, or (the store's own
 * short-circuit) found the mail already `succeeded` — one path, not three.
 *
 * `hooks`/`onAction` no longer exist on `startRun` at all (fix round 2, M4) — this section is kept
 * as the historical record of why a per-call side-channel there is the wrong shape; do not re-add
 * one to `extractionStore.ts` to solve some future problem without re-reading this first.
 */
export async function runAndDeliver(
  mail: MailMessage,
  runDeps: { queryClient?: QueryClient; loadBlob?: BlobLoader },
  callbacks: {
    onSuccess?: (result: ExtractionResult) => void
    onError?: (error: { reason: ExtractionFailureReason; message: string }) => void
  },
): Promise<void> {
  await startRun(mail.id, (dispatch) =>
    runExtraction(mail, {
      dispatch,
      queryClient: runDeps.queryClient,
      loadBlob: runDeps.loadBlob,
    }),
  )
  const result = getPhase(mail.id)
  if (result.status === 'succeeded') callbacks.onSuccess?.(result.result)
  else if (result.status === 'failed') callbacks.onError?.({ reason: result.reason, message: result.message })
  // Anything else here (idle/falling-back/…) would mean `startRun`'s promise resolved without
  // `runExtraction` ever reaching a terminal action — it dispatches exactly one, always (see its
  // own header) — so this branch should be unreachable; fire nothing rather than guess.
}

/**
 * `mutate` itself: `runAndDeliver` above, plus the "consume a cached success exactly once" rule
 * (fix round 1, IMPORTANT). `consumedSuccessIds` is passed in rather than module state — the hook
 * owns one `Set` per mounted instance (`useRef`) and this function is its entire per-call logic,
 * exported and React-free so BOTH branches (cache reused once, then a second call forces a fresh
 * run) are unit-testable without mounting the hook.
 *
 * A mail that has ever succeeded would otherwise never re-run for the page's lifetime — the store
 * has no eviction and short-circuits `startRun` for it. Before pre-warming existed that was fine,
 * because `mutate` always dispatched a fresh `submit`; now the FIRST call after a success is
 * SUPPOSED to reuse the cache (that is the entire pre-warm payoff), so only a call after that one
 * forces a real re-read — e.g. the primary path recovering after a `'local'` fallback result, which
 * must not silently keep re-serving the stale local answer.
 */
export async function mutateAndDeliver(
  mail: MailMessage,
  runDeps: { queryClient?: QueryClient; loadBlob?: BlobLoader },
  callbacks: {
    onSuccess?: (result: ExtractionResult) => void
    onError?: (error: { reason: ExtractionFailureReason; message: string }) => void
  },
  consumedSuccessIds: Set<string>,
): Promise<void> {
  if (getPhase(mail.id).status === 'succeeded' && consumedSuccessIds.has(mail.id)) {
    // Already delivered once — clear the cached result so `runAndDeliver` below genuinely re-runs
    // instead of taking its short-circuit path again. Safe specifically because `resetRun` must
    // only be called from a terminal phase (`extractionStore.ts`'s header, "Safety invariant") —
    // this branch is gated on `status === 'succeeded'`, which is terminal, so no run can be in
    // flight for this `resetRun` to clobber.
    resetRun(mail.id)
    consumedSuccessIds.delete(mail.id)
  }

  await runAndDeliver(mail, runDeps, {
    onSuccess: (result) => {
      consumedSuccessIds.add(mail.id)
      callbacks.onSuccess?.(result)
    },
    onError: callbacks.onError,
  })
}

/**
 * "Read this mail with AI". Not `useMutation`: an attempt can spend most of its life waiting on a
 * socket event with no request in flight, which react-query's single request/response model does
 * not represent. `runExtraction` (above) is the actual orchestration, kept as a plain async
 * function so it is testable without mounting this hook (this repo has no component-test
 * infrastructure — see `mailExtractionState.test.ts`/`useMailExtraction.test.ts`).
 *
 * State itself now lives in `extractionStore.ts`, not a local `useReducer` — a run outlives this
 * component, which is what makes `prewarmExtraction` (above) possible: something started before
 * `CreateEnquiryBar` even mounted is still there when it does.
 */
export function useExtractFromMail(options?: UseExtractFromMailOptions): UseExtractFromMailResult {
  const queryClient = useQueryClient()
  const loadBlob = useCachedAttachmentLoader()

  const optionsRef = useRef(options)
  optionsRef.current = options

  // The mail currently being tracked. `mutate` sets it; until then there is nothing to subscribe
  // to, so an unmounted-and-remounted panel still finds a pre-warmed result waiting.
  const [trackedId, setTrackedId] = useState<string>('')
  // Mirrors `trackedId` for `reset`, which must stay referentially `[]`-stable (its identity used
  // to come from `useReducer`'s stable `dispatchRaw`; reading state directly would make it churn
  // on every mail switch instead).
  const trackedIdRef = useRef(trackedId)
  trackedIdRef.current = trackedId

  // A mail whose cached `succeeded` phase has already been handed to a caller via `onSuccess` —
  // see `mutate` below. Deliberately a ref local to THIS hook instance, not store state: it
  // answers "has the current caller seen this?", not "did this mail ever succeed?", which the
  // store already tracks on `phase` itself.
  const consumedSuccessRef = useRef<Set<string>>(new Set())

  const phase = useSyncExternalStore(
    useCallback((listener: () => void) => (trackedId ? subscribe(trackedId, listener) : () => {}), [trackedId]),
    useCallback(() => (trackedId ? getPhase(trackedId) : IDLE_PHASE), [trackedId]),
  )

  const mutate = useCallback(
    (mail: MailMessage) => {
      setTrackedId(mail.id)
      void mutateAndDeliver(
        mail,
        {
          // Both are pure de-duplication — see `RunExtractionOptions`. The attachment loader is
          // the same one `AttachmentPreview.tsx` uses, so a sheet the purchaser already opened is
          // not downloaded a second time to feed the model.
          queryClient,
          loadBlob,
        },
        {
          onSuccess: (result) => optionsRef.current?.onSuccess?.(result),
          onError: (error) => optionsRef.current?.onError?.(error),
        },
        consumedSuccessRef.current,
      )
    },
    [queryClient, loadBlob],
  )

  const reset = useCallback(() => {
    if (trackedIdRef.current) resetRun(trackedIdRef.current)
    setTrackedId('')
  }, [])

  return {
    phase,
    isPending: phase.status === 'submitting',
    mutate,
    reset,
  }
}
